import React, { useEffect, useState, useRef } from 'react';
import { Button, Form } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePersistedStore } from '../../store/persisted-store';
import PostalCode from '../shared/PostalCode';
import { LeftOutlined, RedoOutlined } from '@ant-design/icons';
import AuthLayout from './AuthLayout';

const Code = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const { login, authentication, auth, clearLoginInfo, clearAuthMessage, getUserInfo, getUserBusinessesAc } = usePersistedStore();

    const mobileNumber = auth.mobile;
    const tokenPassword = auth.tokenPassword;
    const otpLength = auth.otpLength;
    const message = auth.message;
    const loading = auth.loading;
    const resendWaitSeconds = auth.resendWaitSeconds;
    const hasPassword = auth.hasPass;
    const returnUrl = auth.returnUrl;

    const [seconds, setSeconds] = useState(resendWaitSeconds);
    const [disableBtn, setDisableBtn] = useState(true);
    const [input, setInput] = useState({
        value: '',
        valid: [],
    });

    // Use ref to track if we're already submitting
    const isSubmittingRef = useRef(false);
    const submitTimeoutRef = useRef(null);

    // Extend the spin animation by 1s on both ends of the real loading state
    const [spinning, setSpinning] = useState(false);
    const spinTimeoutRef = useRef(null);

    useEffect(() => {
        if (spinTimeoutRef.current) {
            clearTimeout(spinTimeoutRef.current);
        }
        spinTimeoutRef.current = setTimeout(() => {
            setSpinning(loading);
        }, 1000);
        return () => clearTimeout(spinTimeoutRef.current);
    }, [loading]);

    useEffect(() => {
        if (!mobileNumber) {
            navigate('/auth/login' + location.search);
        }
    }, [mobileNumber, navigate, location.search]);

    useEffect(() => {
        let timer;
        if (seconds > 0) {
            timer = setTimeout(() => setSeconds(seconds - 1), 1000);
            setDisableBtn(true);
        } else {
            setDisableBtn(false);
        }
        return () => clearTimeout(timer);
    }, [seconds]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (submitTimeoutRef.current) {
                clearTimeout(submitTimeoutRef.current);
            }
        };
    }, []);

    const handleReturnUrl = () => {
        const searchParams = new URLSearchParams(location.search);
        const returnUrlParam = searchParams.get('returnUrl') || returnUrl;

        if (returnUrlParam && returnUrlParam !== '/' && returnUrlParam !== '/auth') {
            navigate(returnUrlParam);
        } else {
            navigate('/dashboard');
        }
    };

    const submitOtp = async () => {
        // Prevent multiple submissions
        if (isSubmittingRef.current || loading) {
            return;
        }

        if (!input.value || input.value.length !== otpLength) {
            return;
        }

        isSubmittingRef.current = true;

        try {
            const result = await login({
                Code: input.value,
                tokenPassword
            });


            if (result) {
                const userInfo = await getUserInfo();
                if (userInfo?.BusinessToken) {
                    await getUserBusinessesAc();
                    handleReturnUrl();
                }
            }
        } catch (error) {
            console.error('Login error:', error);
        } finally {
            isSubmittingRef.current = false;
        }
    };

    // Handle input change with debounced auto-submit
    const handleInputChange = (value, name, valid) => {

        setInput({ value, valid });

        // Clear any existing timeout
        if (submitTimeoutRef.current) {
            clearTimeout(submitTimeoutRef.current);
        }

        // Auto-submit after a short delay when OTP is complete
        if (value.length === otpLength && seconds > 0 && tokenPassword && !isSubmittingRef.current) {
            submitTimeoutRef.current = setTimeout(() => {
                submitOtp();
            }, 500); // 500ms delay to prevent rapid submissions
        }
    };

    const handleOtp = async () => {
        if ('OTPCredential' in window) {
            const ac = new AbortController();
            try {
                const otp = await navigator.credentials.get({
                    otp: { transport: ['sms'] },
                    signal: ac.signal
                });
                if (otp && otp.code) {
                    handleInputChange(otp.code, '', undefined);
                }
            } catch (err) {
                console.error('OTP retrieval error:', err);
            }
        }
    };

    const handleResendCode = () => {
        // Clear any pending submissions
        if (submitTimeoutRef.current) {
            clearTimeout(submitTimeoutRef.current);
        }
        isSubmittingRef.current = false;

        setInput({ value: '', valid: [] });
        authentication({ MobileNumber: mobileNumber })
            .then(() => {
                setSeconds(resendWaitSeconds);
            });
    };

    const handleManualSubmit = () => {
        // Clear any pending auto-submit
        if (submitTimeoutRef.current) {
            clearTimeout(submitTimeoutRef.current);
        }
        submitOtp();
    };

    return (
        <AuthLayout title="ورود" subtitle={`کد ارسال شده به ${mobileNumber} را وارد کنید`}>
            <div
                onClick={() => {
                    clearLoginInfo();
                    navigate('/auth/login' + location.search);
                }}
                className="mb-3 text-primary"
                style={{ cursor: 'pointer' }}
            >
                اصلاح نام کاربری
            </div>

            <Form layout="vertical">
                <div className="form-group">
                    <PostalCode
                        value={input.value}
                        onChange={handleInputChange}
                        resetErrorMessage={() => clearAuthMessage()}
                        split={Array.from({ length: otpLength }, () => 1)}
                        onFocus={handleOtp}
                        loading={spinning}
                        onKeyPress={(e) => {
                            if (input.value.length === otpLength) {
                                handleManualSubmit();
                            }
                        }}
                    />
                </div>

                {message && (
                    <div className="text-danger mb-3">
                        {message}
                    </div>
                )}

                <div className="text-muted mb-3">
                    {seconds > 0 ? (
                        <span>
                            {String(Math.floor(seconds / 60)).padStart(2, '0')}:{String(seconds % 60).padStart(2, '0')} تا ارسال مجدد کد
                        </span>
                    ) : (
                        <span>می‌توانید کد را مجدداً درخواست کنید</span>
                    )}
                </div>

                {seconds === 0 && (
                    <div className="mb-3">
                        <Button
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                                alignItems: 'center'
                            }}
                            type="link"
                            disabled={disableBtn}
                            onClick={handleResendCode}
                            className="p-0 text-primary"
                        >
                            ارسال مجدد کد پیامکی
                            <RedoOutlined className="ms-1" />
                        </Button>
                    </div>
                )}

                {seconds > 0 && (
                    <div className="mt-3">
                        <Button
                            type="primary"
                            loading={loading}
                            onClick={handleManualSubmit}
                            disabled={input.value.length !== otpLength || seconds === 0}
                            className="btn btn-block waves-effect waves-light"
                            block
                        >
                            ورود
                        </Button>
                    </div>
                )}

                <div className="mt-3 text-center">
                    <Button
                        type="link"
                        onClick={() => {
                            clearAuthMessage();
                            navigate('/auth/password' + location.search);
                        }}
                        className="text-primary"
                    >
                        ورود با رمز عبور
                        <LeftOutlined className="ms-1" />
                    </Button>
                </div>
            </Form>
        </AuthLayout>
    );
};

export default Code;
